const SURVIVABILITY_COEFFICIENTS = {
  timeSinceDiagnosis: -0.04343184,
  previousChemotheraphy: 0.15904238,
  hemoglobin: -0.1252068,
  suvMean: -0.02993874,
  metastasesCount: 0.33636023,
  boneMetastases: 0.15132446,
  liverMetastases: 0.5306651,
};

const SURVIVABILITY_BASE_COEFFICIENTS = {
  survivability12Months: 2.29576542,
  survivability18Months: 6.18045664,
};

const SURVIVABILITY_NOMOGRAM_RISK_THRESHOLD = 197;

const PSA_PROGRESSION_FREE_SURVIVAL_COEFFICIENTS = {
  timeSinceDiagnosis: -0.02945936,
  previousChemotheraphy: 0.1442571,
  suvMean: -0.05174706,
  pelvicLymphNodes: -0.18183009,
  boneMetastases: 0.31149849,
  liverMetastases: 0.39210372,
};

const PSA_PROGRESSION_FREE_SURVIVAL_BASE_COEFFICIENTS = {
  psaProgressionFreeSurvival3Months: 0.456069044,
  psaProgressionFreeSurvival6Months: 1.224874826,
};

const PSA_PROGRESSION_FREE_SURVIVAL_NOMOGRAM_RISK_THRESHOLD = 178;

const LOGISTIC_PSA_50_PERCENT_DECLINE_COEFFICIENTS = {
  previousChemotheraphy: -0.5188555,
  suvMean: 0.1747964,
  pelvicLymphNodes: 0.2640974,
  liverMetastases: -0.5032547,
};

const LOGISTIC_PSA_50_PERCENT_DECLINE_BASE_COEFFICIENT = -1.4324482;
const LOGISTIC_PSA_50_PERCENT_DECLINET_NOMOGRAM_RISK_THRESHOLD = 41;

export function calculateSurvivability(inputs) {
  const baseSurvivabilityRisk = Math.exp(
    SURVIVABILITY_COEFFICIENTS.timeSinceDiagnosis * inputs.timeSinceDiagnosis +
      SURVIVABILITY_COEFFICIENTS.previousChemotheraphy * inputs.previousChemotheraphy +
      SURVIVABILITY_COEFFICIENTS.hemoglobin * inputs.hemoglobin +
      SURVIVABILITY_COEFFICIENTS.suvMean * inputs.suvMean +
      SURVIVABILITY_COEFFICIENTS.metastasesCount * inputs.metastasesCount +
      SURVIVABILITY_COEFFICIENTS.boneMetastases * inputs.boneMetastases +
      SURVIVABILITY_COEFFICIENTS.liverMetastases * inputs.liverMetastases
  );

  return {
    survivability12Months: Math.exp(-1 * baseSurvivabilityRisk * SURVIVABILITY_BASE_COEFFICIENTS.survivability12Months),
    survivability18Months: Math.exp(-1 * baseSurvivabilityRisk * SURVIVABILITY_BASE_COEFFICIENTS.survivability18Months),
  };
}

export function calculatePSAProgressionFreeSurvival(inputs) {
  const basePSAProgressionFreeSurvivalBaseRisk = Math.exp(
    PSA_PROGRESSION_FREE_SURVIVAL_COEFFICIENTS.timeSinceDiagnosis * inputs.timeSinceDiagnosis +
      PSA_PROGRESSION_FREE_SURVIVAL_COEFFICIENTS.previousChemotheraphy * inputs.previousChemotheraphy +
      PSA_PROGRESSION_FREE_SURVIVAL_COEFFICIENTS.suvMean * inputs.suvMean +
      PSA_PROGRESSION_FREE_SURVIVAL_COEFFICIENTS.pelvicLymphNodes * inputs.pelvicLymphNodes +
      PSA_PROGRESSION_FREE_SURVIVAL_COEFFICIENTS.boneMetastases * inputs.boneMetastases +
      PSA_PROGRESSION_FREE_SURVIVAL_COEFFICIENTS.liverMetastases * inputs.liverMetastases
  );

  return {
    psaProgressionFreeSurvival3Months: Math.exp(
      -1 *
        basePSAProgressionFreeSurvivalBaseRisk *
        PSA_PROGRESSION_FREE_SURVIVAL_BASE_COEFFICIENTS.psaProgressionFreeSurvival3Months
    ),
    psaProgressionFreeSurvival6Months: Math.exp(
      -1 *
        basePSAProgressionFreeSurvivalBaseRisk *
        PSA_PROGRESSION_FREE_SURVIVAL_BASE_COEFFICIENTS.psaProgressionFreeSurvival6Months
    ),
  };
}

export function calculateLogisticPSA50PercentDecline(inputs) {
  const baseLogisticPSA50PercentDeclineRisk =
    LOGISTIC_PSA_50_PERCENT_DECLINE_BASE_COEFFICIENT +
    LOGISTIC_PSA_50_PERCENT_DECLINE_COEFFICIENTS.previousChemotheraphy * inputs.previousChemotheraphy +
    LOGISTIC_PSA_50_PERCENT_DECLINE_COEFFICIENTS.suvMean * inputs.suvMean +
    LOGISTIC_PSA_50_PERCENT_DECLINE_COEFFICIENTS.pelvicLymphNodes * inputs.pelvicLymphNodes +
    LOGISTIC_PSA_50_PERCENT_DECLINE_COEFFICIENTS.liverMetastases * inputs.liverMetastases;

  return {
    logisticPSA50PercentDecline: 1 / (1 + Math.exp(-1 * baseLogisticPSA50PercentDeclineRisk)),
  };
}

export function calculateSurvivabilityNomogram(inputs) {
  const points = {
    timeSinceDiagnosis: Math.max(0, Math.min(92, Math.round(92 - (23 / 6) * inputs.timeSinceDiagnosis))),
    previousChemotheraphy: inputs.previousChemotheraphy ? 14 : 0,
    hemoglobin: Math.max(0, Math.min(100, Math.round(1600 / 9 - (100 / 9) * inputs.hemoglobin))),
    metastasesCount: inputs.metastasesCount ? 21 : 0,
    suvMean: Math.max(0, Math.min(57, Math.round(209 / 3 - (19 / 6) * inputs.suvMean))),
    boneMetastases: inputs.boneMetastases ? 17 : 0,
    liverMetastases: inputs.liverMetastases ? 47 : 0,
  };

  const pointsTotal = Object.values(points).reduce((a, b) => a + b, 0);
  const lowRisk = pointsTotal < SURVIVABILITY_NOMOGRAM_RISK_THRESHOLD;

  return {
    ...points,
    pointsTotal,
    lowRisk,
  };
}

export function calculatePSAProgressionFreeSurvivalNomogram(inputs) {
  const points = {
    timeSinceDiagnosis: Math.max(0, Math.min(76, Math.round(76 - (19 / 6) * inputs.timeSinceDiagnosis))),
    previousChemotheraphy: inputs.previousChemotheraphy ? 15 : 0,
    suvMean: Math.max(0, Math.min(100, Math.round(1100 / 9 - (50 / 9) * inputs.suvMean))),
    pelvicLymphNodes: inputs.pelvicLymphNodes ? 0 : 20,
    boneMetastases: inputs.boneMetastases ? 33 : 0,
    liverMetastases: inputs.liverMetastases ? 42 : 0,
  };

  const pointsTotal = Object.values(points).reduce((a, b) => a + b, 0);
  const lowRisk = pointsTotal < PSA_PROGRESSION_FREE_SURVIVAL_NOMOGRAM_RISK_THRESHOLD;

  return {
    ...points,
    pointsTotal,
    lowRisk,
  };
}

export function calculateLogisticPSA50PercentDeclilneNomogram(inputs) {
  const points = {
    previousChemotheraphy: inputs.previousChemotheraphy ? 0 : 22,
    suvMean: Math.max(0, Math.min(100, Math.round(-200 / 9 + (50 / 9) * inputs.suvMean))),
    pelvicLymphNodes: inputs.pelvicLymphNodes ? 14 : 0,
    liverMetastases: inputs.liverMetastases ? 0 : 27,
  };

  const pointsTotal = Object.values(points).reduce((a, b) => a + b, 0);
  const lowRisk = pointsTotal >= LOGISTIC_PSA_50_PERCENT_DECLINET_NOMOGRAM_RISK_THRESHOLD;

  return {
    ...points,
    pointsTotal,
    lowRisk,
  };
}
